import { useEffect, useMemo, useState } from "react";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import Cookies from "js-cookie";
import useAuth from "../hook/useAuth";
import Topbar from "./Topbar";
import Sidebar from "./Sidebar";
import AddUser from "../pages/user/AddUser";
import EditUser from "../pages/user/EditUser";
import DeleteUser from "../pages/user/DeleteUser";
import DeleteChurch from "../pages/church/DeleteChurch";
import DeleteAdminMember from "../pages/admin/DeleteAdminMember";
import DeleteAdminFamilyMember from "../pages/admin/DeleteAdminFamilyMember";
import DeleteSubGroup from "../pages/subgroup/DeleteSubGroup";
import DeleteEvent from "../pages/event/DeleteEvent";
import DeleteFamilyMembers from "../pages/familyMembers/DeleteFamilyMembers";
import DeleteDevice from "../pages/support/DeleteDevice";
import DeletePopup from "./DeletePopup";
import AttachmentView from "./AttachmentView";
import { ConfirmPopup } from "./ConfirmPopup";
import DeleteLeadership from "../pages/leadership/DeleteLeadership";
import DeleteSchedule from "../pages/schedule/DeleteSchedule";
import DeleteLeadershipGroup from "../pages/leadershipGroup/DeleteLeadershipGroup";
import { isPremiumPlan } from "../utils/isPremiumPlan";

const AdminMainLayout = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const { auth, setAuth }: any = useAuth();
    const role = Cookies.get("role-my-cdir") || "";
    const plan = Cookies.get("plan-my-cdir") || "";

    const pathRoot = role?.toLowerCase() === "superadmin" ? "superadmin" : "admin";
    const [navbarHeader, setNavbarHeader] = useState<any[]>([
        ...(role?.toLowerCase() !== "superadmin"
            ? [{
                name: "Help",
                link: `/${pathRoot}/admin-help`,
                isChildVisible: false,
                child: []
            }]
            : []),
        {
            name: "Faq",
            link: `/${pathRoot}/admin-f-a-q`,
            isChildVisible: false,
            child: []
        },
        {
            name: "My Account",
            link: `/${pathRoot}/my-account`,
            isChildVisible: false,
            child: []
        }
    ]);
    const defaultsideBar = useMemo<any[]>(() => ([
        {
            name: "Admin",
            link: `/${pathRoot}`,
            isChildVisible: false,
            child: []
        },
        {
            name: "User Managment",
            link: "",
            isChildVisible: true,
            child: [
                {
                    name: "User",
                    link: `/${pathRoot}/user`,
                    forSuperAdmin: true
                },
                {
                    name: "User Roles",
                    link: `/${pathRoot}/user-roles`,
                    forSuperAdmin: true
                }
            ]
        },
        {
            name: "Organization",
            link: "",
            isChildVisible: true,
            child: [
                {
                    name: "Church",
                    link: `/${pathRoot}/church`,
                    forSuperAdmin: false
                },
                {
                    name: "Ministries",
                    link: `/${pathRoot}/ministries`,
                    forSuperAdmin: false
                }
            ]
        },
        {
            name: "Leadership",
            link: "",
            isChildVisible: true,
            child: [
                {
                    name: "Leadership Team",
                    link: `/${pathRoot}/leadershipTeam`,
                    forSuperAdmin: false
                },
                {
                    name: "Leadership",
                    link: `/${pathRoot}/leadership`,
                    forSuperAdmin: false
                }
            ]
        },
        {
            name: "Family and Members",
            link: "",
            isChildVisible: true,
            child: [
                {
                    name: "Family",
                    link: `/${pathRoot}/family`,
                    forSuperAdmin: false
                },
                {
                    name: "Members",
                    link: `/${pathRoot}/members`,
                    forSuperAdmin: false
                },
                {
                    name: "Invite Members",
                    link: `/${pathRoot}/invite-members`,
                    forSuperAdmin: false
                }
            ]
        },
        {
            name: "Visitors",
            link: "",
            isChildVisible: true,
            child: [
                {
                    name: "Visitor",
                    link: `/${pathRoot}/visitors`,
                    forSuperAdmin: false
                }
            ]
        },
        {
            name: "Schedules and Events",
            link: "",
            isChildVisible: true,
            child: [
                {
                    name: "Schedules",
                    link: `/${pathRoot}/schedule`,
                    forSuperAdmin: false
                },
                {
                    name: "Events",
                    link: `/${pathRoot}/event`,
                    forSuperAdmin: false
                }
            ]
        },
        {
            name: "Push Notification",
            link: "",
            isChildVisible: true,
            child: [
                {
                    name: "Notifications",
                    link: `/${pathRoot}/notifications`,
                    forSuperAdmin: false
                },
                {
                    name: "Logs",
                    link: `/${pathRoot}/notification-logs`,
                    forSuperAdmin: false
                }
            ]
        },
        {
            name: "Logs",
            link: "",
            isChildVisible: true,
            child: [
                {
                    name: "SMS Logs",
                    link: `/${pathRoot}/sms-logs`,
                    forSuperAdmin: false,
                    forPremiumPlan: true
                },
                {
                    name: "Email Logs",
                    link: `/${pathRoot}/email-logs`,
                    forSuperAdmin: false,
                    forPremiumPlan: true
                }
            ]
        },
        {
            name: "Payment",
            link: "",
            isChildVisible: true,
            child: [
                {
                    name: "Payment Gateway",
                    link: `/${pathRoot}/payment-gateway`,
                    forSuperAdmin: true
                },
                {
                    name: "Credit Cards",
                    link: `/${pathRoot}/credit-cards`,
                    forSuperAdmin: true
                }
            ]
        },
        {
            name: "Support",
            link: "",
            isChildVisible: true,
            child: [
                {
                    name: "FAQ",
                    link: `/${pathRoot}/faq`,
                    forSuperAdmin: true,
                },
                {
                    name: "Login Devices",
                    link: `/${pathRoot}/login-devices`,
                    forSuperAdmin: false,
                },
                {
                    name: "Tickets",
                    link: `/${pathRoot}/tickets`,
                    forSuperAdmin: false,
                }
            ]
        },
        {
            name: "Billing",
            link: "",
            isChildVisible: true,
            child: [
                {
                    name: "Billing Details",
                    link: `/${pathRoot}/billing`,
                    forSuperAdmin: false,
                },
            ],
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    ]), []);
    const [sideBar, setSideBar] = useState<any[]>(defaultsideBar);

    const [sideBarHide, setSideBarHide] = useState<boolean>(false);

    useEffect(() => {
        const isAdmin = auth?.roles?.toString()?.toLowerCase().replaceAll(" ", "") === "admin";

        const isPremium = role?.toLowerCase() === "superadmin" || isPremiumPlan(plan);

        if (isAdmin) {
            const result = defaultsideBar
                .map((item: any) => {
                    const newChild = item.child.filter((child: any) => {
                        // Hide SuperAdmin-only items
                        if (child.forSuperAdmin) {
                            return false;
                        }

                        // Hide premium-only items unless plan is Premium
                        if (child.forPremiumPlan === true && !isPremium) {
                            return false;
                        }

                        return true;
                    });

                    if (newChild.length > 0) {
                        return {
                            ...item,
                            child: newChild
                        };
                    }

                    return null;
                })
                .filter((item: any) => item !== null);

            setSideBar(result);
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [auth, plan]);

    useEffect(() => {
        const headerForAdmin = "admin";
        if (location.pathname.replace(/\/+$/, "") === `/${headerForAdmin}`) {
            if (auth?.roles?.toString()?.toLowerCase().replaceAll(" ", "") === "admin") {
                navigate(`/${pathRoot}/church`, { replace: true });
            } else if (auth?.roles?.toString()?.toLowerCase().replaceAll(" ", "") === "superadmin") {
                navigate(`/${pathRoot}`, { replace: true });
            }
        }

        const hideHeaderForAdminFaq = "admin-faq";

        if (location.pathname.replace("/", "").endsWith(hideHeaderForAdminFaq)) {
            setSideBarHide(true);
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [location]);

    const logOut = () => {
        setAuth(null);
        Cookies.remove("role-my-cdir");
        Cookies.remove("token-my-cdir");
        Cookies.remove("groupId-my-cdir");
        Cookies.remove("userId-my-cdir");
        Cookies.remove("isAdmin-my-cdir");
        Cookies.remove("plan-my-cdir");
        Cookies.remove("expires-my-cdir");
        navigate(role?.toLowerCase() === "superadmin" ? "/superadmin-sign-in" : "/sign-in", { state: { from: location }, replace: true });
    }

    return (
        <section className="admin___layout__container">
            <Topbar
                navbarHeader={navbarHeader}
                setNavbarHeader={setNavbarHeader}
                logOut={logOut}
            />

            <div className="divider-view w-full mt-[72px]" />

            <section className="admin__body__container flex flex-row">
                <Sidebar
                    sideBar={sideBar}
                    setSideBar={setSideBar}
                    sideBarHide={sideBarHide}
                    setSideBarHide={setSideBarHide}
                />

                <section className={`admin__main__container min-h-[calc(100vh-72px)] transform duration-500 px-2 py-4 ${sideBarHide ? "z-0 ml-8 mr-2 min-w-[calc(100%-40px)]" : "-z-10 lg:z-0 lg:ml-64 min-w-[calc(100%-160px)] lg:min-w-[calc(100%-256px)]"}`}>
                    <Outlet />
                </section>
            </section>

            {location.pathname.replace("/", "").includes("admin/user/addUser") && <AddUser />}
            {location.pathname.replace("/", "").includes("admin/user/editUser") && <EditUser />}
            <DeleteUser />
            <ConfirmPopup />
            <DeleteChurch />
            <DeleteLeadershipGroup />
            <DeleteLeadership />
            <DeleteSubGroup />

            <DeleteAdminMember />
            <DeleteAdminFamilyMember />
            <DeleteFamilyMembers />
            <DeleteFamilyMembers />

            <DeleteEvent />
            <DeleteSchedule />
            <DeleteDevice />
            <DeletePopup />

            <AttachmentView />
        </section>
    )
}

export default AdminMainLayout;